import React, { useState } from 'react';
import { cn } from 'lib/utils';
import config from 'config';

/** Rewrites to the API proxy, which streams the file whether or not the bucket is public. */
function resolveAvatarSrc(src?: string): string | undefined {
  if (!src) return undefined;
  const match = src.match(/avatars\/([^/?#]+)\/(?:(\d+)-)?[^/?#]+/);
  if (!match) return src;
  const base = `${config.api.base_url}/api/users/${match[1]}/avatar`;
  return match[2] ? `${base}?v=${match[2]}` : base;
}

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** The signed-in user's own avatar takes the lime; everyone else's initials sit on a neutral disc. */
  isCurrentUser?: boolean;
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({ src, alt, name, size = 'md', isCurrentUser = false, className }) => {
  const [imageError, setImageError] = useState(false);

  const sizes = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg',
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const resolvedSrc = resolveAvatarSrc(src);
  const showImage = resolvedSrc && !imageError;

  return (
    <div
      className={cn(
        // overflow-hidden so a non-square photo is clipped to the avatar shape
        // instead of poking out of the corners; leading-none keeps initials
        // optically centred rather than sitting on a taller line box.
        // People are always circular — the one shape in the system that is not
        // derived from `--radius`, so an avatar reads as a person on every
        // surface rather than as another rounded control.
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold leading-none',
        isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/70',
        sizes[size],
        className
      )}
    >
      {showImage ? (
        <img
          src={resolvedSrc}
          alt={alt || name || 'Avatar'}
          className="h-full w-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <span>{getInitials(name)}</span>
      )}
    </div>
  );
};

export { Avatar };
export default Avatar;
